import { Controller } from "@hotwired/stimulus";
import VotersGridController, {
  MAX_VISIBLE_ROWS,
  type GridRowError,
  type RowLevel,
} from "../../append_voters/voters_grid_component/voters_grid_component_controller";
import type ConfirmDeleteModalController from "../../application/toolkit/confirm_delete_modal_component/confirm_delete_modal_component_controller";
import { showToast, type ToastType } from "../../application/toolkit/toast_component/show_toast";

const GRID_CONTROLLER_ID = "append-voters--voters-grid-component--voters-grid-component";
const MODAL_CONTROLLER_ID = "application--toolkit--confirm-delete-modal-component--confirm-delete-modal-component";
const DEFAULT_STALE_MESSAGE = "List changed on the server — review the status column and validate again.";

type StatusKind = "success" | "warning" | "error";
type RowStatus = "found" | "voted" | "already_spoiled" | "not_found" | "blank" | "duplicate" | "ambiguous";
type MatchKey = "identifier" | "email";

interface StatusMessage {
  label: string;
  level?: RowLevel;
}

interface ValidationCounts {
  total: number;
  found: number;
  voted: number;
  already_spoiled: number;
  not_found: number;
  blank: number;
  duplicate: number;
  ambiguous: number;
  spoilable: number;
}

interface ValidationRow {
  index: number;
  status: RowStatus;
  invitation_id: number | null;
}

interface ValidationReport {
  match_key: MatchKey;
  valid: boolean;
  has_voted: boolean;
  counts: ValidationCounts;
  rows: ValidationRow[];
}

interface ParseCsvResponse {
  ids: string[];
  header_skipped: boolean;
  row_count: number;
}

interface CommitResponse {
  status?: string;
  spoiled_count?: number;
  skipped_already_spoiled?: number;
  skipped_voted?: number;
  redirect_url?: string;
  message?: string;
  failed_count?: number;
  report?: ValidationReport;
}

interface ErrorBody {
  status?: string;
  message?: string;
  max_rows?: number;
  report?: ValidationReport;
  redirect_url?: string;
  spoiled_count?: number;
  failed_count?: number;
}

const csrfToken = (): string => {
  const meta = document.querySelector('meta[name="csrf-token"]');
  return meta ? (meta.getAttribute("content") || "") : "";
};

const postJSON = (url: string, payload: unknown): Promise<Record<string, unknown>> =>
  fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-CSRF-Token": csrfToken(),
    },
    body: JSON.stringify(payload),
    credentials: "same-origin",
  }).then(async (res) => {
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return Promise.reject({ status: res.status, body });
    return body as Record<string, unknown>;
  });

export default class extends Controller {
  static targets = [
    "methodSelector",
    "typingPanel",
    "csvUploadPanel",
    "csvFileInput",
    "googleSheetsPanel",
    "overlay",
    "statusMessage",
    "resultMessage",
    "reasonInput",
    "votedPanel",
    "validateButton",
    "updateButton",
    "confirmOverlay",
    "confirmCount",
    "confirmButtonCount",
  ];

  static values = {
    validateUrl: String,
    parseCsvUrl: String,
    commitUrl: String,
    cancelUrl: String,
    importGoogleSheetUrl: String,
    matchKey: String,
    maxRows: Number,
    statusMessages: { type: Object, default: {} },
  };

  declare readonly methodSelectorTarget: HTMLElement;
  declare readonly typingPanelTarget: HTMLElement;
  declare readonly csvUploadPanelTarget: HTMLElement;
  declare readonly csvFileInputTarget: HTMLInputElement;
  declare readonly googleSheetsPanelTarget: HTMLElement;
  declare readonly overlayTarget: HTMLElement;
  declare readonly statusMessageTarget: HTMLElement;
  declare readonly resultMessageTarget: HTMLElement;
  declare readonly reasonInputTarget: HTMLTextAreaElement;
  declare readonly votedPanelTarget: HTMLElement;
  declare readonly validateButtonTarget: HTMLButtonElement;
  declare readonly updateButtonTarget: HTMLButtonElement;
  declare readonly confirmOverlayTarget: HTMLElement;
  declare readonly confirmCountTarget: HTMLElement;
  declare readonly confirmButtonCountTarget: HTMLElement;

  declare readonly hasCsvUploadPanelTarget: boolean;
  declare readonly hasCsvFileInputTarget: boolean;
  declare readonly hasGoogleSheetsPanelTarget: boolean;
  declare readonly hasOverlayTarget: boolean;
  declare readonly hasStatusMessageTarget: boolean;
  declare readonly hasResultMessageTarget: boolean;
  declare readonly hasReasonInputTarget: boolean;
  declare readonly hasVotedPanelTarget: boolean;
  declare readonly hasValidateButtonTarget: boolean;
  declare readonly hasUpdateButtonTarget: boolean;
  declare readonly hasConfirmOverlayTarget: boolean;
  declare readonly hasConfirmCountTarget: boolean;
  declare readonly hasConfirmButtonCountTarget: boolean;

  declare validateUrlValue: string;
  declare parseCsvUrlValue: string;
  declare commitUrlValue: string;
  declare cancelUrlValue: string;
  declare importGoogleSheetUrlValue: string;
  declare matchKeyValue: MatchKey;
  declare maxRowsValue: number;
  declare statusMessagesValue: Record<string, StatusMessage>;

  private validated = false;

  private report: ValidationReport | null = null;

  connect(): void {
    const checked = this.element.querySelector<HTMLInputElement>('input[name="spoil_import_method"]:checked');
    this.showMethodPanels(checked?.value || "typing_or_copying");
    this.hide(this.overlayTarget);
    this.hideVotedPanel();
    this.updateActionState();
  }

  selectMethod(event: Event): void {
    const method = (event.target as HTMLInputElement).value;
    this.showMethodPanels(method);
    this.resetValidationState();
    this.clearResult();
  }

  async validateList(event?: Event): Promise<void> {
    event?.preventDefault();

    const grid = this.gridController();
    if (!grid) {
      this.setResult("Please wait for the table to finish loading.", "error");
      return;
    }

    grid.clearRowErrors();
    const ids = this.gatherIds(grid);
    if (ids.length === 0) {
      this.resetValidationState();
      this.setResult(`Enter at least one ${this.matchKeyLabel()}.`, "error");
      return;
    }

    this.setBusy("Validating list...");

    try {
      const response = await postJSON(this.validateUrlValue, { ids });
      this.applyReport(response as unknown as ValidationReport);
    } catch (error) {
      const payload = error as { status: number; body: ErrorBody };
      const message = this.errorMessage(payload);
      this.resetValidationState();
      this.setResult(message, "error");
      this.showToast(message, "danger");
    } finally {
      this.clearBusy();
    }
  }

  async handleCsvFileSelected(event: Event): Promise<void> {
    const target = event.target as HTMLInputElement | null;
    if (!target || target.type !== "file") return;

    const file = this.csvFileInputTarget.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    this.setBusy("Reading CSV...");

    try {
      const response = await fetch(this.parseCsvUrlValue, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": csrfToken(),
        },
        credentials: "same-origin",
        body: formData,
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw { status: response.status, body };

      await this.loadParsedIds(body as ParseCsvResponse);
    } catch (error) {
      const payload = error as { status: number; body: ErrorBody };
      const message = this.errorMessage(payload);
      this.setResult(message, "error");
      this.showToast(message, "danger");
    } finally {
      this.clearBusy();
      // Without this, re-selecting the same (corrected) file name fires no `change` event.
      target.value = "";
    }
  }

  // Google Sheets picker handed back a file: validate the detail, then import it exactly like a
  // dropped CSV (switch to the typing panel, load ids, auto-validate).
  handlePickerFilePicked(event: CustomEvent<{ fileId?: string; accessToken?: string }>): void {
    const detail = (event && event.detail) || {};
    if (!detail.fileId || !detail.accessToken) {
      this.showError("Could not read file from Google Picker.");
      return;
    }
    void this.importGoogleSheet({ source: "picker", source_params: { file_id: detail.fileId, access_token: detail.accessToken } });
  }

  handleUrlSubmitted(event: CustomEvent<{ url?: string }>): void {
    const url = event && event.detail && event.detail.url;
    if (!url) {
      this.showError("Please paste a Google Sheets URL.");
      return;
    }
    void this.importGoogleSheet({ source: "public_url", source_params: { url } });
  }

  private async importGoogleSheet(payload: { source: string; source_params: Record<string, string> }): Promise<void> {
    this.setBusy("Importing Google Sheet...");

    try {
      const body = await postJSON(this.importGoogleSheetUrlValue, payload);
      await this.loadParsedIds(body as unknown as ParseCsvResponse);
    } catch (error) {
      const payload2 = error as { status: number; body: ErrorBody };
      const message = this.errorMessage(payload2);
      this.setResult(message, "error");
      this.showToast(message, "danger");
    } finally {
      this.clearBusy();
    }
  }

  // Shared by the CSV drop and the Google Sheets import: load the parsed ids into the grid,
  // switch to the typing panel, and auto-validate exactly the same way for both paths.
  private async loadParsedIds(payload: ParseCsvResponse): Promise<void> {
    const grid = this.gridController();
    if (!grid) {
      this.setResult("Please wait for the table to finish loading.", "error");
      return;
    }

    this.selectMethodByValue("typing_or_copying");
    grid.restoreDefaultSize();
    grid.loadRows(payload.ids || []);
    await this.validateList();
  }

  private showError(message: string): void {
    this.setResult(message, "error");
    this.showToast(message, "danger");
  }

  handleListEdited(): void {
    const grid = this.gridController();
    grid?.restoreDefaultSize();
    if (!this.validated) return;

    grid?.clearRowErrors();
    this.validated = false;
    this.report = null;
    this.hideVotedPanel();
    this.updateActionState();
    this.setResult("List changed — validate again.", "warning");
  }

  handleGridTruncated(event: Event): void {
    const detail = (event as CustomEvent<{ maxRows?: number }>).detail || {};
    const maxRows = detail.maxRows ?? this.maxRowsValue;
    this.setResult(`You can spoil at most ${maxRows} voters at a time. Rows beyond that limit were not added.`, "warning");
  }

  handleReasonInput(): void {
    this.updateActionState();
  }

  // Toggling the voted radio changes spoilableCount() (R4) without touching the grid or reason.
  handleIncludeVotedChange(): void {
    this.updateActionState();
  }

  returnFocusToUpdate(): void {
    if (!this.commitModalVisible()) return;

    window.setTimeout(() => {
      if (this.hasUpdateButtonTarget && this.updateButtonTarget.isConnected) this.updateButtonTarget.focus();
    }, 0);
  }

  handleCommitModalBackdrop(event: Event): void {
    if (!this.commitModalVisible()) return;
    if (event.target !== this.confirmOverlayTarget) return;

    this.returnFocusToUpdate();
  }

  openCommitModal(event?: Event): void {
    event?.preventDefault();
    if (!this.report || !this.validated) return;

    const count = this.spoilableCount();
    if (count <= 0) return;

    this.syncCommitCounts(count);
    this.modalController()?.open(event || new MouseEvent("click"));
  }

  async confirmCommit(event?: Event): Promise<void> {
    event?.preventDefault();
    if (!this.report || !this.validated) return;

    const grid = this.gridController();
    if (!grid) {
      this.setResult("Please wait for the table to finish loading.", "error");
      return;
    }

    const ids = this.gatherIds(grid);
    const includeVoted = this.includeVoted();
    this.closeCommitModal();
    this.setBusy("Spoiling voters...");

    try {
      const response = await postJSON(this.commitUrlValue, {
        ids,
        spoil_reason: this.reasonInputTarget.value,
        include_voted: includeVoted,
        // R2/AC-17: lets the server detect a voter acting between Validate and Update.
        expected_counts: {
          found: this.report.counts.found,
          voted: this.report.counts.voted,
          already_spoiled: this.report.counts.already_spoiled,
        },
      });
      const result = response as unknown as CommitResponse;
      if (result.redirect_url) {
        window.location.assign(result.redirect_url);
        return;
      }

      this.validated = false;
      this.updateActionState();
      this.setResult(result.message || "Voters updated.", "success");
      if (result.status === "nothing_to_spoil" && result.message) {
        this.showToast(result.message, "success");
      }
    } catch (error) {
      const payload = error as { status: number; body: ErrorBody };
      if (payload.status === 409 && payload.body.report) {
        const message = payload.body.message || DEFAULT_STALE_MESSAGE;
        this.applyReport(payload.body.report, message, "error");
        this.showToast(message, "danger");
      } else {
        this.validated = false;
        this.updateActionState();
        if (payload.body.status === "partial_failure") {
          this.setPartialFailureResult(payload.body, "warning");
          this.showToast(payload.body.message || "Some voters could not be spoiled.", "danger");
        } else {
          const message = this.errorMessage(payload);
          this.setResult(message, "error");
          this.showToast(message, "danger");
        }
      }
    } finally {
      this.clearBusy();
    }
  }

  private applyReport(report: ValidationReport, guidance?: string, kind?: StatusKind): void {
    const grid = this.gridController();
    grid?.showRowErrors(report.rows.map((row) => this.toGridRowError(row)));
    if (report.counts.total < MAX_VISIBLE_ROWS) {
      grid?.fitToRows(report.counts.total);
    } else {
      grid?.restoreDefaultSize();
    }

    this.report = report;
    this.validated = report.valid;
    if (report.has_voted) {
      this.show(this.votedPanelTarget);
    } else {
      this.hideVotedPanel();
    }
    this.updateActionState();

    const defaultGuidance = report.valid
      ? "Validation passed. Add a spoil reason to enable Update."
      : "Fix or remove the marked rows and validate again.";
    this.setValidationResult(
      report,
      guidance || defaultGuidance,
      kind || (report.valid ? "success" : "error"),
    );
  }

  private gatherIds(grid: VotersGridController): string[] {
    const rows = grid.gatherRows();

    return rows.map((row) => {
      const value = row[this.matchKeyValue];
      return value === null || value === undefined ? "" : String(value);
    });
  }

  private toGridRowError(row: ValidationRow): GridRowError {
    const status = this.statusMessagesValue[row.status] || { label: row.status, level: "error" };

    return {
      index: row.index,
      fields: { [this.matchKeyValue]: [status.label] },
      message: status.label,
      level: status.level || "error",
    };
  }

  private summaryParts(report: ValidationReport): Array<{ count: number; label: string }> {
    const order: RowStatus[] = [
      "found",
      "voted",
      "already_spoiled",
      "not_found",
      "blank",
      "duplicate",
      "ambiguous",
    ];

    return order.map((status) => ({
      count: report.counts[status],
      label: this.summaryLabel(status),
    }));
  }

  private summaryLabel(status: RowStatus): string {
    const labels: Record<RowStatus, string> = {
      found: "found",
      voted: "voted",
      already_spoiled: "already spoiled",
      not_found: "not found",
      blank: "blank",
      duplicate: "duplicate",
      ambiguous: "ambiguous",
    };

    return labels[status];
  }

  private includeVoted(): boolean {
    const checked = this.element.querySelector<HTMLInputElement>('input[name="include_voted"]:checked');
    return checked?.value === "true";
  }

  private hideVotedPanel(): void {
    const noOption = this.element.querySelector<HTMLInputElement>('input[name="include_voted"][value="false"]');
    if (noOption) noOption.checked = true;
    this.hide(this.votedPanelTarget);
  }

  private reasonPresent(): boolean {
    return this.reasonInputTarget.value.trim().length > 0;
  }

  // R4: nothing to commit once found + (voted, if included) is zero -- e.g. every row was
  // already spoiled, or all voted and the admin left the radio on No.
  private spoilableCount(): number {
    if (!this.report) return 0;

    return this.report.counts.found + (this.includeVoted() ? this.report.counts.voted : 0);
  }

  private updateActionState(): void {
    this.setUpdateEnabled(this.validated && this.spoilableCount() > 0 && this.reasonPresent());
  }

  // ButtonComponent renders both the `disabled` attribute and the
  // `toolkit-button-component--disabled` class (pointer-events: none); toggling only the
  // attribute leaves the button visually and functionally stuck disabled.
  private setUpdateEnabled(enabled: boolean): void {
    this.updateButtonTarget.disabled = !enabled;
    this.updateButtonTarget.classList.toggle('toolkit-button-component--disabled', !enabled);
  }

  private selectMethodByValue(value: string): void {
    const radio = this.element.querySelector<HTMLInputElement>(`input[name="spoil_import_method"][value="${value}"]`);
    if (radio) radio.checked = true;
    this.showMethodPanels(value);
  }

  private showMethodPanels(method: string | null): void {
    if (method === "csv_upload") {
      this.hide(this.typingPanelTarget);
      this.show(this.csvUploadPanelTarget);
      if (this.hasGoogleSheetsPanelTarget) this.hide(this.googleSheetsPanelTarget);
      return;
    }

    if (method === "google_sheets") {
      this.hide(this.typingPanelTarget);
      this.hide(this.csvUploadPanelTarget);
      if (this.hasGoogleSheetsPanelTarget) this.show(this.googleSheetsPanelTarget);
      return;
    }

    this.show(this.typingPanelTarget);
    this.hide(this.csvUploadPanelTarget);
    if (this.hasGoogleSheetsPanelTarget) this.hide(this.googleSheetsPanelTarget);
  }

  private resetValidationState(): void {
    const grid = this.gridController();

    grid?.restoreDefaultSize();
    grid?.clearRowErrors();
    this.validated = false;
    this.report = null;
    this.hideVotedPanel();
    this.updateActionState();
  }

  private setBusy(message: string): void {
    if (this.hasStatusMessageTarget) this.statusMessageTarget.textContent = message;
    if (this.hasValidateButtonTarget) this.validateButtonTarget.disabled = true;
    if (this.hasUpdateButtonTarget) this.setUpdateEnabled(false);
    this.show(this.overlayTarget);
  }

  private clearBusy(): void {
    if (this.hasStatusMessageTarget) this.statusMessageTarget.textContent = "";
    if (this.hasValidateButtonTarget) this.validateButtonTarget.disabled = false;
    this.updateActionState();
    this.hide(this.overlayTarget);
  }

  private setResult(message: string, kind: StatusKind): void {
    this.resultMessageTarget.textContent = message;
    this.showResult(kind);
  }

  private setValidationResult(report: ValidationReport, guidance: string, kind: StatusKind): void {
    const target = this.resultMessageTarget;
    target.textContent = "";

    const summaryLine = document.createElement("div");
    summaryLine.className = "spoil-voters__status-line spoil-voters__status-line--summary";
    summaryLine.appendChild(document.createTextNode("Checked "));
    summaryLine.appendChild(this.strongText(String(report.counts.total)));
    summaryLine.appendChild(document.createTextNode(` rows. ${guidance}`));

    const countsLine = document.createElement("div");
    countsLine.className = "spoil-voters__status-line spoil-voters__status-line--counts";
    this.summaryParts(report).forEach(({ count, label }, index) => {
      if (index > 0) countsLine.appendChild(document.createTextNode(" · "));
      countsLine.appendChild(this.strongText(String(count)));
      countsLine.appendChild(document.createTextNode(` ${label}`));
    });

    target.appendChild(summaryLine);
    target.appendChild(countsLine);
    this.showResult(kind);
  }

  private strongText(text: string): HTMLElement {
    const strong = document.createElement("strong");
    strong.textContent = text;
    return strong;
  }

  // Built with createElement rather than innerHTML (zero-innerHTML policy): body.message and
  // the Voter Status URL are server-controlled today (I18n + route helper), but this keeps it
  // that way even if a future caller feeds in something less trusted.
  private setPartialFailureResult(body: ErrorBody, kind: StatusKind): void {
    const target = this.resultMessageTarget;
    target.textContent = "";

    if (body.message) target.appendChild(document.createTextNode(`${body.message} `));

    const url = body.redirect_url || this.cancelUrlValue;
    if (url) {
      const link = document.createElement("a");
      link.href = url;
      link.textContent = "Return to Voter Status";
      target.appendChild(link);
      target.appendChild(document.createTextNode("."));
    }

    this.showResult(kind);
  }

  private showResult(kind: StatusKind): void {
    this.resultMessageTarget.classList.remove("hide", "inline_success", "inline_warning", "inline_error");
    this.resultMessageTarget.classList.add(this.statusClass(kind));
  }

  private statusClass(kind: StatusKind): string {
    return `inline_${kind}`;
  }

  private errorMessage(error: { status: number; body: ErrorBody }): string {
    if (error.body.message) return error.body.message;
    if (error.body.status === "too_many_rows") {
      return `You can spoil at most ${error.body.max_rows ?? this.maxRowsValue} voters at a time.`;
    }
    if (error.status === 422) return "The request could not be processed.";
    return "Something went wrong. Try again.";
  }

  private syncCommitCounts(count: number): void {
    if (this.hasConfirmCountTarget) this.confirmCountTarget.textContent = String(count);
    if (this.hasConfirmButtonCountTarget) this.confirmButtonCountTarget.textContent = String(count);
  }

  private commitModalVisible(): boolean {
    return this.hasConfirmOverlayTarget && !this.confirmOverlayTarget.hasAttribute("hidden");
  }

  private closeCommitModal(): void {
    this.modalController()?.close();
  }

  private modalController(): ConfirmDeleteModalController | null {
    const modalElement = this.element.querySelector<HTMLElement>(`[data-controller~="${MODAL_CONTROLLER_ID}"]`);
    if (!modalElement) return null;

    return this.application.getControllerForElementAndIdentifier(
      modalElement,
      MODAL_CONTROLLER_ID,
    ) as ConfirmDeleteModalController | null;
  }

  private showToast(message: string, type: ToastType): void {
    showToast(message, type);
  }

  private matchKeyLabel(): string {
    return this.element.querySelector(".ag-header-cell-text")?.textContent ||
      (this.matchKeyValue === "identifier" ? "ID" : "Email");
  }

  private clearResult(): void {
    this.resultMessageTarget.textContent = "";
    this.resultMessageTarget.classList.add("hide");
    this.resultMessageTarget.classList.remove("inline_success", "inline_warning", "inline_error");
  }

  private show(element: HTMLElement): void {
    element.classList.remove("hide");
  }

  private hide(element: HTMLElement): void {
    element.classList.add("hide");
  }

  private gridController(): VotersGridController | null {
    const gridElement = this.element.querySelector<HTMLElement>(`[data-controller~="${GRID_CONTROLLER_ID}"]`);
    if (!gridElement) return null;

    return this.application.getControllerForElementAndIdentifier(
      gridElement,
      GRID_CONTROLLER_ID,
    ) as VotersGridController | null;
  }
}
